import * as Sentry from "@sentry/nextjs";
import { z } from "zod";
import { triggerPracticeAppointmentTypeEffectiveAvailabilitySync } from "@/background/practice/on-practice-appointment-type-effective-availability-sync";
import { provisionManagedChildEventsForHost } from "@/data/scheduling/provision-managed-child-events";
import {
  type SyncPracticeAppointmentTypeChildEventMappingsResult,
  syncPracticeAppointmentTypeChildEventMappings,
} from "@/data/scheduling/sync-practice-appointment-type-child-event-mappings";
import { CalClient } from "@/lib/cal/client";
import { normalizeEffectiveAvailabilitySyncReason } from "@/lib/calendar/effective-availability-sync-reasons";
import { createBackgroundJob } from "@/lib/inngest/utils";
import { createMedplumClient } from "@/lib/medplum/backend";

export const event = "vibrant/PracticeAppointmentTypeChildEventMappings.sync";

const InputSchema = z.object({
  practiceId: z.string().min(1),
  teamId: z.number().int().positive(),
  appointmentTypeId: z.string().min(1).optional(),
  actorProviderId: z.string().min(1).optional(),
  reason: z.enum(["provider-added", "provider-removed", "appointment-type-updated"]).optional(),
  calUserId: z.number().int().nonnegative().optional(),
});

type SyncPayload = z.infer<typeof InputSchema>;

const RETRY_DELAYS = ["10s", "30s"] as const;

export const onPracticeAppointmentTypeChildEventMappingsSync = createBackgroundJob({
  event,
  id: "practice-appointment-type-child-event-mappings-sync",
  schema: InputSchema,
  // event.id: syncs can repeat for same practice+type (e.g. adding multiple providers)
  idempotency: "event.id",
  handler: async ({ payload, step, logger }) => {
    // A provider who joins a practice team after its appointment types were created has no Cal
    // managed child events, so every booking for them 404s until Cal is nudged to provision them
    // (ENG-5216). Do that before mirroring, so the mirror sync below has real children to read.
    // Best-effort: a provisioning failure must not block the mirror sync, which still has value and
    // will retry provisioning on the next provider-added event.
    if (payload.reason === "provider-added" && typeof payload.calUserId === "number") {
      const calUserId = payload.calUserId; // narrowed to number by the guard above
      try {
        const provisionResult = await step.run(
          "provision-managed-child-events-for-new-host",
          async () => {
            return provisionManagedChildEventsForHost({
              calTeamClient: CalClient.forTeam(),
              teamId: payload.teamId,
              calUserId,
              logger,
            });
          },
        );
        logger.info("Provisioned managed child events before mirror sync", {
          practiceId: payload.practiceId,
          providerId: payload.actorProviderId ?? null,
          teamId: payload.teamId,
          calUserId,
          ...provisionResult,
        });
        if (provisionResult.failedCount > 0) {
          // Partial failure: the host may be unbookable on the appointment types that failed to
          // provision. Surface it (it was previously swallowed silently) — but do NOT throw here,
          // which would block the mirror sync below for the whole retry window. Non-blocking retry
          // of the failed parents is tracked in ENG-5240.
          logger.warn("Managed child provisioning had per-parent failures", {
            practiceId: payload.practiceId,
            providerId: payload.actorProviderId ?? null,
            teamId: payload.teamId,
            calUserId,
            failedCount: provisionResult.failedCount,
            scannedParents: provisionResult.scannedParents,
          });
          Sentry.captureMessage("Managed child provisioning had per-parent failures", {
            level: "warning",
            tags: {
              feature: "cal-managed-child-provisioning",
              practiceId: payload.practiceId,
              providerId: payload.actorProviderId ?? null,
            },
            extra: {
              teamId: payload.teamId,
              calUserId,
              failedCount: provisionResult.failedCount,
              scannedParents: provisionResult.scannedParents,
            },
          });
        }
      } catch (error) {
        // A provisioning failure is isolated from the mirror sync (which still has value), but the
        // condition it represents — this provider cannot be booked — is actionable, not transient.
        // Surface it to Sentry so it doesn't hide behind a warn line (ENG-5216).
        logger.warn("Failed to provision managed child events before mirror sync", {
          practiceId: payload.practiceId,
          providerId: payload.actorProviderId ?? null,
          teamId: payload.teamId,
          calUserId,
          error: error instanceof Error ? error.message : String(error),
        });
        Sentry.captureException(error, {
          tags: {
            feature: "cal-managed-child-provisioning",
            practiceId: payload.practiceId,
            providerId: payload.actorProviderId ?? null,
          },
          extra: { teamId: payload.teamId, calUserId },
        });
      }
    }

    const maxAttempts = RETRY_DELAYS.length + 1;
    let childEventMappingResult: SyncPracticeAppointmentTypeChildEventMappingsResult | undefined;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const result = (await step.run(
          `sync-practice-child-event-mappings-attempt-${attempt}`,
          async (): Promise<SyncPracticeAppointmentTypeChildEventMappingsResult> => {
            const medplum = createMedplumClient({ audience: "background" });
            return syncPracticeAppointmentTypeChildEventMappings(medplum, {
              practiceId: payload.practiceId,
              teamId: payload.teamId,
              appointmentTypeId: payload.appointmentTypeId,
              reason: payload.reason,
              expectedCalUserId: payload.calUserId,
            });
          },
        )) as SyncPracticeAppointmentTypeChildEventMappingsResult;

        logger.info("Synced child event mappings", {
          ...payload,
          attempt,
          ...result,
        });

        // Flag when sync completes but produces zero updated mappings.
        // Post-migration, this means appointment types still have empty calChildEventTypes
        // and scheduling will silently return zero slots (ENG-3280 root cause).
        if (result.updatedCount === 0 && result.scannedCount > 0) {
          logger.warn(
            "Child event mapping sync scanned appointment types but updated none — scheduling may be broken",
            {
              practiceId: payload.practiceId,
              teamId: payload.teamId,
              appointmentTypeId: payload.appointmentTypeId,
              reason: payload.reason,
              scannedCount: result.scannedCount,
              skippedCount: result.skippedCount,
              unchangedCount: result.unchangedCount,
            },
          );
        }

        childEventMappingResult = result;
        break;
      } catch (error) {
        const isFinalAttempt = attempt >= maxAttempts;
        const errorMessage = error instanceof Error ? error.message : String(error);

        if (isFinalAttempt) {
          logger.error("Child event mapping sync failed after retries", {
            ...payload,
            attempt,
            error: errorMessage,
          });
          throw error instanceof Error ? error : new Error(errorMessage);
        }

        const retryDelay = RETRY_DELAYS[attempt - 1];
        logger.warn("Child event mapping sync failed, retrying", {
          ...payload,
          attempt,
          retryDelay,
          error: errorMessage,
        });
        await step.sleep(`wait-before-child-event-mapping-sync-retry-${attempt}`, retryDelay);
      }
    }

    if (!childEventMappingResult) {
      throw new Error("Failed to sync child event mappings");
    }

    const effectiveAvailabilityReason = normalizeEffectiveAvailabilitySyncReason({
      reason: payload.reason,
    });

    try {
      await step.run("queue-effective-availability-sync", async (): Promise<void> => {
        await triggerPracticeAppointmentTypeEffectiveAvailabilitySync({
          practiceId: payload.practiceId,
          appointmentTypeId: payload.appointmentTypeId,
          actorProviderId: payload.actorProviderId,
          reason: effectiveAvailabilityReason,
        });
      });
    } catch (error) {
      logger.warn("Failed to queue effective availability sync after child event mapping sync", {
        ...payload,
        reason: effectiveAvailabilityReason,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error instanceof Error ? error : new Error(String(error));
    }

    return childEventMappingResult;
  },
});

export async function triggerPracticeAppointmentTypeChildEventMappingsSync(
  payload: SyncPayload,
): Promise<void> {
  await onPracticeAppointmentTypeChildEventMappingsSync.trigger(payload);
}
